import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Car, 
  CheckCircle, 
  Clock, 
  Shield, 
  ThumbsUp, 
  Mail,
  ChevronRight,
  Users,
  BadgeCheck,
  Calculator,
  Repeat,
  HelpCircle,
  Menu,
  X
} from "lucide-react";
import { useState } from "react";

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="landing-page">
      <Helmet>
        <title>Moto Money Finance | Vehicle Financing in Canada</title>
        <meta name="description" content="Get approved for vehicle financing in Canada. We help Canadians with all credit situations find the right vehicle and financing options. Apply online today!" />
        <meta property="og:title" content="Moto Money Finance | Vehicle Financing in Canada" />
        <meta property="og:description" content="Get approved for vehicle financing in Canada. We help Canadians with all credit situations find the right vehicle and financing options." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_i-want-this/artifacts/feojhpuf_IMG_9678.jpeg" />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="nav-links desktop-nav">
            <Link to="/how-it-works" className="nav-link">How It Works</Link>
            <Link to="/calculator" className="nav-link">Calculator</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
            <Link to="/apply" className="btn-apply" data-testid="header-apply-btn">
              Apply Now
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav" data-testid="mobile-nav">
            <Link to="/how-it-works" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
            <Link to="/calculator" onClick={() => setMobileMenuOpen(false)}>Calculator</Link>
            <Link to="/refer-a-friend" onClick={() => setMobileMenuOpen(false)}>Refer a Friend</Link>
            <Link to="/trade-in" onClick={() => setMobileMenuOpen(false)}>Trade-In</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
            <Link to="/apply" className="btn-apply" onClick={() => setMobileMenuOpen(false)}>Apply Now</Link>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Driving Made Easy</h1>
          <p className="hero-subtitle">
            Everyone deserves a great vehicle. We're here to help you find the right vehicle and financing options.
          </p>
          <div className="hero-features">
            <div className="hero-feature">
              <CheckCircle size={20} />
              <span>Rates as Low as 0% O.A.C.*</span>
            </div>
            <div className="hero-feature">
              <CheckCircle size={20} />
              <span>$0 Down Payment Options</span>
            </div>
            <div className="hero-feature">
              <CheckCircle size={20} />
              <span>Fast & Easy Online Application</span>
            </div>
          </div>
          <div className="hero-cta">
            <Link to="/apply" className="btn-hero-primary" data-testid="hero-apply-btn">
              Get Started Today
              <ChevronRight size={20} />
            </Link>
          </div>
          <p className="oac-disclaimer">
            *Available on select vehicles for qualified applicants. Subject to lender approval.
          </p>
        </div>
        <div className="hero-image">
          <img 
            src="https://customer-assets.emergentagent.com/job_i-want-this/artifacts/feojhpuf_IMG_9678.jpeg" 
            alt="Luxury Vehicles - Mustang, RAM, Mercedes"
          />
        </div>
      </section>

      {/* Trust Indicators - Cleaned up */}
      <section className="trust-section">
        <div className="trust-container">
          <div className="trust-item">
            <Users size={28} />
            <div>
              <strong>All Credit</strong>
              <span>Welcome</span>
            </div>
          </div>
          <div className="trust-item">
            <Clock size={28} />
            <div>
              <strong>Fast</strong>
              <span>Follow-Up</span>
            </div>
          </div>
          <div className="trust-item">
            <Shield size={28} />
            <div>
              <strong>Secure</strong>
              <span>Application</span>
            </div>
          </div>
          <div className="trust-item">
            <BadgeCheck size={28} />
            <div>
              <strong>100%</strong>
              <span>Free Service</span>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Logos */}
      <section className="brands-section">
        <div className="brands-container">
          <p className="brands-title">We Work With All Brands</p>
          <div className="brands-grid">
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/ford-logo.png" alt="Ford" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/chevrolet-logo.png" alt="Chevrolet" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/dodge-logo.png" alt="Dodge" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/audi-logo.png" alt="Audi" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/mercedes-benz-logo.png" alt="Mercedes-Benz" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/toyota-logo.png" alt="Toyota" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/honda-logo.png" alt="Honda" />
            </div>
            <div className="brand-logo">
              <img src="https://www.carlogos.org/car-logos/bmw-logo.png" alt="BMW" />
            </div>
          </div>
        </div>
      </section>

      {/* Service Cards - NEW */}
      <section className="services-section">
        <div className="services-container">
          <h2>How Can We Help?</h2>
          <p className="section-subtitle">
            Whether you're looking to finance a vehicle, trade in your current one, or refer a friend, we've got you covered.
          </p>
          
          <div className="services-grid">
            <Link to="/apply" className="service-card" data-testid="service-apply">
              <div className="service-icon">
                <Car size={32} />
              </div>
              <h3>Apply for Financing</h3>
              <p>Fill out our simple application and let our team help you find financing options.</p>
              <span className="service-link">
                Start Application <ChevronRight size={16} />
              </span>
            </Link>

            <Link to="/calculator" className="service-card" data-testid="service-calculator">
              <div className="service-icon">
                <Calculator size={32} />
              </div>
              <h3>Payment Calculator</h3>
              <p>Estimate your monthly payment based on vehicle price, down payment, and terms.</p>
              <span className="service-link">
                Calculate Payment <ChevronRight size={16} />
              </span>
            </Link>

            <Link to="/trade-in" className="service-card" data-testid="service-tradein">
              <div className="service-icon">
                <Repeat size={32} />
              </div>
              <h3>Trade-In Enquiry</h3>
              <p>Have a vehicle to trade? Submit details and let our team help with your next purchase.</p>
              <span className="service-link">
                Submit Trade-In <ChevronRight size={16} />
              </span>
            </Link>

            <Link to="/refer-a-friend" className="service-card" data-testid="service-refer">
              <div className="service-icon">
                <Users size={32} />
              </div>
              <h3>Refer a Friend</h3>
              <p>Know someone looking for a vehicle? Refer them to Moto Money Finance.</p>
              <span className="service-link">
                Make a Referral <ChevronRight size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="features-section">
        <div className="features-container">
          <h2>Why Canadians Choose Moto Money Finance</h2>
          <p className="section-subtitle">
            We work with Canadians in all credit situations. Our goal is to help you find the right vehicle and financing options.
          </p>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <ThumbsUp size={32} />
              </div>
              <h3>All Credit Welcome</h3>
              <p>
                Whether you have excellent credit or are rebuilding, we work with Canadians in all credit situations.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Clock size={32} />
              </div>
              <h3>Fast Process</h3>
              <p>
                Our streamlined online application takes just minutes to complete. Our team will follow up to discuss your options.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={32} />
              </div>
              <h3>Safe & Secure</h3>
              <p>
                Your personal information is protected. We never sell your data to third parties.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Car size={32} />
              </div>
              <h3>Vehicle Selection</h3>
              <p>
                Work with our team to explore vehicle options that fit your needs and budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="process-section">
        <div className="process-container">
          <h2>Get Started in 4 Simple Steps</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Apply Online</h3>
              <p>Fill out our simple online application in just a few minutes.</p>
            </div>
            <div className="process-arrow">
              <ChevronRight size={32} />
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Team Follow-Up</h3>
              <p>Our team reviews your application and contacts you to discuss options.</p>
            </div>
            <div className="process-arrow">
              <ChevronRight size={32} />
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Review Options</h3>
              <p>Explore available vehicles and financing options that fit your needs.</p>
            </div>
            <div className="process-arrow">
              <ChevronRight size={32} />
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Complete Paperwork</h3>
              <p>Finalize your financing and get ready to drive your new vehicle.</p>
            </div>
          </div>
          
          <div className="process-cta">
            <Link to="/how-it-works" className="btn-process" data-testid="process-learn-btn">
              Learn More
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Credit Situations */}
      <section className="credit-section">
        <div className="credit-container">
          <h2>We Help Canadians With All Credit Situations</h2>
          <p className="section-subtitle">
            Everyone deserves a great vehicle. We're here to help you explore your options.
          </p>
          
          <div className="credit-grid">
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>Excellent Credit</span>
            </div>
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>Rebuilding Credit</span>
            </div>
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>First-Time Buyers</span>
            </div>
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>New to Canada</span>
            </div>
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>Self-Employed</span>
            </div>
            <div className="credit-item">
              <CheckCircle size={24} />
              <span>All Situations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2>What Our Customers Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card" data-testid="testimonial-flodel">
              <p className="testimonial-text">
                "Used Moto Money Finance for my car and the process was simple and hassle-free. Good communication, better rate than the dealer offered, and everything was sorted quickly. Happy with the service and would recommend them."
              </p>
              <div className="testimonial-author">
                <span className="author-name">Flodel</span>
              </div>
            </div>
            <div className="testimonial-card" data-testid="testimonial-aman">
              <p className="testimonial-text">
                "Solid experience with Moto Money Finance. Got approved fast even with average credit, good rate, and no hassle collecting the car. Would use them again."
              </p>
              <div className="testimonial-author">
                <span className="author-name">Aman</span>
              </div>
            </div>
            <div className="testimonial-card" data-testid="testimonial-mike">
              <p className="testimonial-text">
                "Used Moto Money Finance and it was straightforward. Quick approval, clear communication, and no issues picking up the car. Happy with how it went."
              </p>
              <div className="testimonial-author">
                <span className="author-name">Mike</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="faq-teaser-section">
        <div className="faq-teaser-container">
          <HelpCircle size={40} />
          <h2>Have Questions?</h2>
          <p>
            Check out our FAQ for answers to common questions about financing, applying, trade-ins, and more.
          </p>
          <Link to="/faq" className="btn btn-secondary" data-testid="faq-link">
            View FAQ
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2>Ready to Get Started?</h2>
          <p>
            Apply online today and take the first step toward your next vehicle.
          </p>
          <Link to="/apply" className="btn-cta" data-testid="footer-apply-btn">
            Apply Now — It's Free
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <Car size={28} />
              <span>Moto Money<strong>Finance</strong></span>
            </Link>
            <p>Driving Made Easy</p>
            <p className="footer-tagline">
              Helping Canadians find vehicle financing options. We work with all credit situations.
            </p>
          </div>
          
          <div className="footer-links">
            <h4>Quick Links</h4>
            <Link to="/apply">Apply Now</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/calculator">Payment Calculator</Link>
            <Link to="/refer-a-friend">Refer a Friend</Link>
            <Link to="/trade-in">Trade-In</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
          
          <div className="footer-contact">
            <h4>Contact Us</h4>
            <a href="mailto:sales@motomoneyfinance.com">
              <Mail size={16} />
              sales@motomoneyfinance.com
            </a>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
